import { call, put, select, takeEvery, takeLatest, debounce } from 'redux-saga/effects';
import { matchPath } from 'connected-react-router';
// (`stopSubmit` from redux-form was used here for per-field error
// display on update failure. After Epic 13f the form is RHF-driven;
// per-field errors land via the snackbar only — restore via
// `setError` if needed.)
import { getLocation, goBack, LOCATION_CHANGE } from 'connected-react-router';
import { actions, constants, selector } from './redux';
import Api from '../../utils/Api';
import { addSuccess } from '../notifications/actions';

export function* fetchItems() {
  try {
    const featureState = yield select(selector);
    const json = yield call(Api.crewChange.index, featureState);

    yield put(actions.addItems(json));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

// Same shape as the `getCRUDSagas` regression fixed under QA bug
// 3.2: the success branch never dispatched `ITEM_UPDATED_SUCCESSFULLY`
// / `ITEM_DELETED_SUCCESSFULLY`, so `isPushing` stayed stuck and the
// local store didn't reflect the save until a hard refetch. On the
// crew-changes form the bug manifests as QA bug 6.3 — the form
// disappears (goBack fires) but the row in the list shows the old
// values until the LOCATION_CHANGE-driven refetch lands; if the
// refetch errors the user sees a blank page. Dispatch the success
// action so the reducer's `updateItem` / `deleteItem` handler runs
// and the form/list both come out the other side in a clean state.
function* updateItem({ payload }) {
  try {
    const { pathname } = yield select(getLocation);
    const { params: { id } } = matchPath(pathname, { path: '/crew-changes/:id/edit' });

    const { data } = yield call(Api.crewChange.update, id, payload);
    if (data && data.data) yield put(actions.updateItem(data.data));
    yield put(goBack());

    yield put(addSuccess('Crew change updated successfully'));
  } catch (e) {
    yield put(actions.updateItemError(e));
  }
}

function* deleteItem() {
  try {
    const pathname = yield select((s) => s.router.location.pathname);
    const { params: { id } } = matchPath(pathname, { path: '/crew-changes/:id/delete' });

    yield call(Api.crewChange.delete, id);
    yield put(actions.deleteItem(id));
    yield put(goBack());

    yield put(addSuccess('Crew change deleted successfully'));
  } catch (e) {
    console.error(e);

    yield put(actions.deleteItemError(e));
  }
}

function* fetchAfterSearch() {
  yield put(actions.applySearch()); // reset current pagination page

  yield put(actions.loadItemsRequest());
}

export function* fetchItemsPageInit({ payload: { isFirstRendering, location: { pathname } } }) {
  const didOpenPage = (
    matchPath(pathname, { exact: true, path: '/crew-changes' })
    || (isFirstRendering && matchPath(pathname, { path: '/crew-changes/:id/edit' }))
  );

  if (didOpenPage) yield put(actions.loadItemsRequest());
}


/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 * @returns {ForkEffect[]}
 */
export default function getFeatureSagas() {
  return [
    // takeLatest (CREW-1268): a newer request cancels the one in flight, so an
    // older answer arriving last cannot overwrite the list.
    takeLatest([
      constants.SORT_ITEMS,
      constants.APPLY_FILTER,
      constants.CHANGE_PAGE_NUMBER,
      constants.CHANGE_PAGE_ITEMS_COUNT,
      constants.LOAD_ITEMS_REQUEST,
    ], fetchItems),
    takeEvery(constants.UPDATE_ITEM_REQUEST, updateItem),
    takeEvery(constants.DELETE_ITEM_REQUEST, deleteItem),
    takeEvery(LOCATION_CHANGE, fetchItemsPageInit),
    debounce(700, constants.CHANGE_SEARCH, fetchAfterSearch),
  ];
}
